/**
 * Feinheiten der Zeichen- und Absatzformatierung, die die Momentaufnahme aus
 * `document.ts` nicht mitführt.
 *
 * Hoch- und Tiefstellen sowie Durchstreichen gibt es seit WordApi 1.1.
 * Kapitälchen, Großbuchstaben, Absatzrahmen und Schattierung erst mit
 * WordApiDesktop 1.3 – also nur in Word für Windows und Mac. Fehlt der Satz,
 * sind die betreffenden Angaben `null`, und die Aufgabe weicht auf das
 * Prüfmakro aus.
 */

import { supportsWordApiDesktop, wordApiDesktop } from "./capabilities";
import { isWordAvailable, WordUnavailableError } from "./document";

export interface ZeichenRun {
  text: string;
  paragraphIndex: number;
  /** `null` bedeutet: gemischt – ein Teil des Wortes ist hochgestellt (etwa „m²“). */
  superscript: boolean | null;
  subscript: boolean | null;
  strikeThrough: boolean | null;
  /** `null`, wenn gemischt oder WordApiDesktop 1.3 fehlt. */
  smallCaps: boolean | null;
  allCaps: boolean | null;
}

export interface AbsatzDekoration {
  index: number;
  text: string;
  /** Hat der Absatz mindestens eine sichtbare Rahmenlinie? */
  hatRahmen: boolean;
  /** Hintergrundfarbe der Schattierung, `null` ohne Schattierung. */
  schattierung: string | null;
}

export interface ZeichenSnapshot {
  runs: ZeichenRun[];
  /** Kapitälchen und Großbuchstaben lesbar (WordApiDesktop 1.3)? */
  desktopDetails: boolean;
  /** `null`, wenn WordApiDesktop 1.3 fehlt. */
  absaetze: AbsatzDekoration[] | null;
}

function boolOderNull(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

/** Farbwerte, die „keine Schattierung“ bedeuten. */
function istKeineFarbe(farbe: string): boolean {
  const wert = farbe.trim().toLowerCase();
  return wert === "" || wert === "automatic" || wert === "auto" || wert === "#ffffff" || wert === "none";
}

export async function readZeichen(): Promise<ZeichenSnapshot> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  const desktop = supportsWordApiDesktop(wordApiDesktop.zeichenDetails);

  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();

    const properties = ["text", "font/superscript", "font/subscript", "font/strikeThrough"];
    if (desktop) properties.push("font/smallCaps", "font/allCaps");
    const rangeLoad = properties.map((property) => `items/${property}`).join(",");

    const ranges = paragraphs.items.map((paragraph) => {
      if (!paragraph.text || paragraph.text.trim().length === 0) return null;
      const collection = paragraph.getTextRanges([" "], false);
      collection.load(rangeLoad);
      return collection;
    });

    const dekoration = desktop
      ? paragraphs.items.map((paragraph) => {
          const borders = paragraph.borders;
          borders.load("items/isVisible,items/lineStyle");
          const shading = paragraph.shading;
          shading.load("backgroundPatternColor");
          return { borders, shading };
        })
      : null;

    try {
      await context.sync();
    } catch {
      // Einzelne Desktop-Eigenschaften können in Zwischenständen von Word
      // fehlen. Dann ohne sie weiterlesen, statt die Prüfung scheitern zu lassen.
      return readZeichenOhneDesktop();
    }

    const runs: ZeichenRun[] = ranges.flatMap((collection, paragraphIndex) =>
      (collection?.items ?? [])
        .map((range) => ({
          text: (range.text ?? "").trim(),
          paragraphIndex,
          superscript: boolOderNull(range.font.superscript),
          subscript: boolOderNull(range.font.subscript),
          strikeThrough: boolOderNull(range.font.strikeThrough),
          smallCaps: desktop ? boolOderNull(range.font.smallCaps) : null,
          allCaps: desktop ? boolOderNull(range.font.allCaps) : null,
        }))
        .filter((run) => run.text.length > 0),
    );

    const absaetze: AbsatzDekoration[] | null = dekoration
      ? dekoration.map(({ borders, shading }, index) => {
          const farbe =
            typeof shading.backgroundPatternColor === "string" ? shading.backgroundPatternColor : "";
          return {
            index,
            text: (paragraphs.items[index].text ?? "").trim(),
            hatRahmen: borders.items.some(
              (border) => border.isVisible === true && border.lineStyle !== "None",
            ),
            schattierung: istKeineFarbe(farbe) ? null : farbe,
          };
        })
      : null;

    return { runs, desktopDetails: desktop, absaetze };
  });
}

/** Ersatzlauf ohne die Desktop-Eigenschaften. */
async function readZeichenOhneDesktop(): Promise<ZeichenSnapshot> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    const ranges = paragraphs.items.map((paragraph) => {
      if (!paragraph.text || paragraph.text.trim().length === 0) return null;
      const collection = paragraph.getTextRanges([" "], false);
      collection.load("items/text,items/font/superscript,items/font/subscript,items/font/strikeThrough");
      return collection;
    });
    await context.sync();
    const runs = ranges.flatMap((collection, paragraphIndex) =>
      (collection?.items ?? [])
        .map((range) => ({
          text: (range.text ?? "").trim(),
          paragraphIndex,
          superscript: boolOderNull(range.font.superscript),
          subscript: boolOderNull(range.font.subscript),
          strikeThrough: boolOderNull(range.font.strikeThrough),
          smallCaps: null,
          allCaps: null,
        }))
        .filter((run) => run.text.length > 0),
    );
    return { runs, desktopDetails: false, absaetze: null };
  });
}

/** Wörter, die ganz oder teilweise hochgestellt sind. */
export function hochgestellt(snapshot: ZeichenSnapshot): ZeichenRun[] {
  return snapshot.runs.filter((run) => run.superscript !== false);
}

/** Wörter, die ganz oder teilweise tiefgestellt sind. */
export function tiefgestellt(snapshot: ZeichenSnapshot): ZeichenRun[] {
  return snapshot.runs.filter((run) => run.subscript !== false);
}
